/**
 * CourseContentService.js
 * 
 * Handles fetching and saving generated course content from/to database.
 * Content is generated once via LLM and cached in DB for all students.
 */

import { API_BASE_URL, apiFetch } from '../config';

/**
 * Helper function to get selected class_section and university from localStorage
 * Uses the master selected_section (set by main page dropdown), falls back to first section
 */
const getClassInfo = () => {
  const selectedSection = localStorage.getItem('selected_section') 
    || (localStorage.getItem('class_section') || '').split(',')[0].trim();
  return {
    class_section: selectedSection,
    university: localStorage.getItem('university') || ''
  };
};

/**
 * Check if content exists in database
 * @param {string} slide - Slide ID (e.g., "slide01")
 * @param {string} subject - Subject/topic name (e.g., "Variables")
 * @returns {Object|null} - Content object if found, null if not
 */
export const getCourseContent = async (slide, subject) => {
  try {
    const { class_section, university } = getClassInfo();
    
    const params = new URLSearchParams({
      slide: slide,
      subject: subject,
      class_section: class_section,
      university: university
    });
    
    const response = await apiFetch(`${API_BASE_URL}/api/course-content?${params}`);
    
    if (!response.ok) {
      console.error('[CourseContentService] GET failed:', response.status);
      return null;
    }
    
    const text = await response.text();
    if (!text || !text.trim()) {
      console.log('[CourseContentService] Content not found for:', slide, subject);
      return null;
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch (parseErr) {
      console.error('[CourseContentService] JSON parse error:', parseErr);
      return null;
    }

    // Backend returns null if not found
    if (data === null) {
      console.log('[CourseContentService] Content not found for:', slide, subject);
      return null;
    }
    
    console.log('[CourseContentService] Content found for:', slide, subject);
    return data;
    
  } catch (error) {
    console.error('[CourseContentService] Error fetching content:', error);
    return null;
  }
};

/**
 * Save generated content to database
 * @param {string} slide - Slide ID (e.g., "slide01")
 * @param {string} subject - Subject/topic name (e.g., "Variables")
 * @param {string} content - Generated HTML/text content
 * @param {string} username - Username of who generated it (or "auto")
 * @returns {boolean} - true if saved successfully, false otherwise
 */
export const saveCourseContent = async (slide, subject, content, username) => {
  try {
    const { class_section, university } = getClassInfo();
    
    const response = await apiFetch(`${API_BASE_URL}/api/course-content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        slide: slide,
        subject: subject,
        content: content,
        username: username || 'auto',
        class_section: class_section,
        university: university
      })
    });
    
    if (!response.ok) {
      console.error('[CourseContentService] POST failed:', response.status);
      return false;
    }
    
    const result = await response.json();
    console.log('[CourseContentService] Content saved:', result);
    return result === true;
    
  } catch (error) {
    console.error('[CourseContentService] Error saving content:', error);
    return false;
  }
};
